import React, { useState, useEffect, useRef, useCallback } from 'react';
import SinFichero from './SinFichero';
import { getSignedUrl } from '../../../../../services/s3Service';

const ImageMessage = ({ details }) => {
  const { imageUrl, maytapiImageUrl, s3Key, filename, caption } = details;
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const isRenewingRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 2;

  // Function to renew S3 signed URL
  const renewSignedUrl = useCallback(async () => {
    if (!s3Key || isRenewingRef.current) return null;

    isRenewingRef.current = true;
    try {
      const signedUrl = await getSignedUrl(s3Key);
      if (signedUrl) {
        retryCountRef.current = 0; // Reset retry count on success
        isRenewingRef.current = false;
        return signedUrl;
      }
    } catch (error) {
      console.error('Error renewing signed URL:', error);
    }
    isRenewingRef.current = false;
    return null;
  }, [s3Key]);

  useEffect(() => {
    // If we have an S3 key, always try to get a fresh signed URL
    // This ensures we have a valid URL even if the initial one is expired
    if (s3Key) {
      const initializeUrl = async () => {
        const freshUrl = await renewSignedUrl();
        if (freshUrl) {
          setCurrentImageUrl(freshUrl);
        } else if (maytapiImageUrl) {
          // Fallback to MayTapi URL only if we couldn't get S3 URL
          setCurrentImageUrl(maytapiImageUrl);
        } else if (imageUrl) {
          // Use original imageUrl as last resort
          setCurrentImageUrl(imageUrl);
        }
      };
      initializeUrl();
    } else if (maytapiImageUrl) {
      // Fallback to MayTapi URL if no S3 key
      setCurrentImageUrl(maytapiImageUrl);
    } else if (imageUrl) {
      // Use original imageUrl as last resort
      setCurrentImageUrl(imageUrl);
    }
  }, [s3Key, renewSignedUrl, maytapiImageUrl, imageUrl]);

  const handleImageError = async () => {
    // If S3 URL fails and we have an s3Key, try to renew it
    if (s3Key && retryCountRef.current < maxRetries) {
      retryCountRef.current += 1;
      const renewedUrl = await renewSignedUrl();
      if (renewedUrl) {
        setCurrentImageUrl(renewedUrl);
        return;
      }
    }

    // If renewal failed or no s3Key, try MayTapi URL as fallback
    if (maytapiImageUrl && currentImageUrl !== maytapiImageUrl) {
      setCurrentImageUrl(maytapiImageUrl);
    }
  };

  // Sin fichero NO se devuelve null: eso hacía desaparecer del chat el mensaje
  // entero, y un justificante de pago que no se ve es peor que uno roto.
  if (!currentImageUrl) return <SinFichero details={details} />;

  return (
    <div className='space-y-1'>
      <a href={currentImageUrl} target='_blank' rel='noreferrer'>
        <img src={currentImageUrl} alt={filename || 'Imagen'} className='rounded-lg max-h-64' onError={handleImageError} />
      </a>
      {caption && <div className='text-xs text-gray-600 whitespace-pre-wrap break-words'>{caption}</div>}
    </div>
  );
};

export default ImageMessage;
